import { type User } from '@/portalApi'
import { portalApi } from '@/util/apiClient'
import { queryClient } from '@/util/queryClient'
import Toasts from '@/util/toasts'
import { fullName } from '@/util/users'
import { useQuery } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, watch } from 'vue'

/**
 * The shared list of user accounts: the assignee dropdown's options, and the
 * names shown beside anything a user did.
 *
 * The list is held in the query cache, which the admin store invalidates after
 * every user mutation, so a user created or updated there is reflected here
 * rather than staying as the tab first read it.
 */

/** The admin store invalidates and reads through this same entry. */
export const USERS_QUERY_KEY = ['users']

// Matches the administration reads: long enough that moving between views does
// not read the list again, short enough that another administrator's change
// lands without a reload.
const USERS_STALE_MS = 5 * 60 * 1000

export const usersQuery = () => ({
  queryKey: USERS_QUERY_KEY,
  queryFn: () => portalApi.usersGet(),
  staleTime: USERS_STALE_MS,
})

const byFamilyName = (a: User, b: User) =>
  (a.family_name || '').localeCompare(b.family_name || '')

export const useUsersStore = defineStore('users', () => {
  const { data, isFetching, dataUpdatedAt, isError, error, refetch } = useQuery(
    {
      ...usersQuery(),
      select: (users: User[]) =>
        [...users]
          .sort(byFamilyName)
          .map((user) => ({ ...user, name: fullName(user) })),
    },
    queryClient,
  )

  watch(isError, (failed) => {
    if (failed) {
      console.error(error.value)
      Toasts.Error('Error fetching users; See console for details')
    }
  })

  const items = computed(() => data.value ?? [])

  // When the list was last read, so callers can tell a list that has not loaded
  // from one that loaded empty.
  const fetched = computed(() =>
    dataUpdatedAt.value ? new Date(dataUpdatedAt.value) : undefined,
  )

  const byId = (_id: string) => items.value.find((a) => a.id === _id)

  return {
    byId,
    fetched,
    loadingItems: isFetching,
    fetchUsers: refetch,
    items,
  }
})
